import { action, computed, makeObservable, override } from 'mobx';
import { createContext, useContext } from 'react';
import { redirectToRoute } from 'routes/redirectToRoute';
import { SubscriberRouteV2Id } from 'routes/routesConfigV2';
import EmployeeFlowDelegate from 'store/flows/subscriber/employeeFlowDelegate';
import IMedicalCategoryPage, {
  MedicalCategoryPageDomainParams,
} from 'store/modules/health-questionnaire/iMedicalCategoryPage';

export default class MedicalCategoryPageStore extends IMedicalCategoryPage {
  public readonly flowDelegate: EmployeeFlowDelegate;

  public get isInitialLoading(): boolean {
    return this.flowDelegate.isInitialDataLoading || this.isPageContentLoading;
  }

  public returnBack(): void {
    const { groupId, subscriberId, entityId } = this.domainParams;
    redirectToRoute(SubscriberRouteV2Id.SUBSCRIBER_HEALTH_QUESTIONNAIRE, { groupId, subscriberId, entityId });
  }

  protected navigateToCategory(categoryName: string): void {
    const { groupId, subscriberId, entityId } = this.domainParams;

    redirectToRoute(SubscriberRouteV2Id.SUBSCRIBER_HEALTH_QUESTIONNAIRE_MEDICAL_CATEGORY, {
      groupId,
      subscriberId,
      entityId,
      category: encodeURIComponent(categoryName),
    });
  }

  public override async getInitialData(): Promise<void> {
    this.flowDelegate.setIsInitialDataLoading(true);
    await super.getInitialData();
    this.flowDelegate.setIsInitialDataLoading(false);
  }

  constructor(domainParams: MedicalCategoryPageDomainParams) {
    super(domainParams);
    this.flowDelegate = new EmployeeFlowDelegate({
      domainParams,
      nextPageId: SubscriberRouteV2Id.SUBSCRIBER_HEALTH_QUESTIONNAIRE,
    });

    makeObservable<MedicalCategoryPageStore, 'navigateToCategory'>(this, {
      isInitialLoading: computed,
      returnBack: action.bound,
      navigateToCategory: action.bound,
      getInitialData: override,
    });
  }
}

const MedicalCategoryPageStoreContext = createContext(
  new MedicalCategoryPageStore({ groupId: '', subscriberId: '', entityId: '', category: '' }),
);

export const useMedicalCategoryPageStore = (): MedicalCategoryPageStore => {
  return useContext(MedicalCategoryPageStoreContext);
};

export { MedicalCategoryPageStoreContext };
